import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import clsx from 'clsx';
import trans from 'translate';
import PropTypes from 'prop-types';
import TextField from './TextField';
import SelectField from './SelectField';
import { cleanObj, fromFieldsToMockValues } from '../../../../utils/mapper';
import './Fields.scss';
import { addPropsToChildren } from '../../Stepper/Stepper';
import InfoHint from '../../InfoHint/InfoHint';
import { wait } from '../../../../services/utilsService';

const Field = ({ field, error, ...rest }) => {
  switch (field.tag) {
    case 'select':
      return <SelectField {...rest} {...field} error={error} />;
    default:
      return <TextField {...rest} {...field} error={error} />;
  }
};

Field.propTypes = {
  field: PropTypes.object.isRequired,
  error: PropTypes.string.isRequired,
};

export default function Fields({
  fields = [],
  initialValues,
  onSubmit,
  validationSchema = yup.object().shape({}),
  children,
  onValuesChanges,
  formStyle,
  wrapperClass,
}) {
  const [values, setValues] = useState({});
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState([]);
  const valuesMergedValue = Object.values(values).join(' ');

  const rawTouched = Object.entries(touched)
    .filter(([key, value]) => value)
    .map(([key, value]) => key);

  // const mockInitialValues = useMemo(() => fromFieldsToMockValues(fields, ''), [fields]);
  const stringifiedVersionOfInitialValues = JSON.stringify(initialValues ?? {});
  const initialValuesWithMock = useMemo(() => ({ ...values, ...initialValues }), [initialValues]);

  const handleChange = (e) => {
    const value = e?.target?.value;
    const name = e?.target?.name;
    const validatedValue = value;
    const dataObj = {
      [name]: validatedValue,
    };
    setValues((_values) => ({ ..._values, ...dataObj }));
  };
  const handleBlur = ({ target: { name } }) => setTouched({ ...touched, [name]: true });

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched({});
    onSubmit(values);
  };

  useEffect(() => {
    onValuesChanges(values, setValues, errors);
  }, [valuesMergedValue]);

  const fromValuesToTouched = () => {
    let result = {}; // array of { [fieldName]: true } for touched fields
    Object.entries({ ...initialValues, ...initialValues } ?? {}).forEach(([key, value]) => {
      if (Number.isFinite(value) || value) result = { ...result, [key]: true };
    });
    setTouched({ ...touched, ...result });
  };

  useEffect(() => {
    setValues({ ...values, ...initialValues });
    fromValuesToTouched(); // update touched based on coming values
  }, [initialValues]);

  const validate = async () => {
    await wait(200);
    validationSchema
      ?.validate(values, { abortEarly: false })
      .then(() => {
        setErrors({});
      })
      .catch(({ inner }) => {
        const innerErrors = inner
          .map((innerError) => ({ [innerError.params.path]: innerError.errors }))
          .reduce((prev, curr) => ({ ...prev, ...curr }));
        setErrors(innerErrors);
      });
  };

  useEffect(() => {
    validate(values);
  }, [values, initialValuesWithMock]);

  const getError = useCallback(
    (field) => {
      const allFieldsErrors = errors?.[field.name];
      const showError = (touched?.[field.name] || values?.[field.name]) && !!allFieldsErrors;
      const errorMsg = (showError && allFieldsErrors) || '';
      return { showError, errorMsg };
    },
    [errors],
  );
  const formDisabled = !!Object.keys(errors)?.length;

  const passedProps = {
    values,
    setValues,
  };

  return (
    <form className="fields-form" onSubmit={handleSubmit} style={formStyle}>
      <div
        className={clsx('default-fields-wrapper d-flex flex-wrap flex-between gap-md', wrapperClass)}
        onSubmit={handleSubmit}
      >
        {fields.map((field) => (
          <div key={field.id} className={clsx('custom-field-wrapper', field.className)}>
            <div className="label-wrapper">
              {field.label && <label htmlFor={field.id}>{trans(field.label)}</label>}
              {!!field.secondHint && (
                <div className="field-tooltip-hint">
                  {addPropsToChildren(field.secondHint, { ...passedProps, field })}
                </div>
              )}
              {field.infoHint && <InfoHint title={field.infoHint} />}
            </div>
            <div className="field-container">
              {field?.renderField ? (
                field?.renderField({ values, errors, touched, setValues, field, setTouched })
              ) : field?.render ? (
                field?.render()
              ) : (
                <>
                  <Field
                    onBlur={handleBlur}
                    error={getError(field).errorMsg || ''}
                    field={{ ...field, value: values?.[field.name] }}
                    onChange={(e) => (field?.onChange ? field?.onChange?.(e) : handleChange(e))}
                  />
                </>
              )}
              {!!field.inlineHint && <div className="field-inline-hint">{field.inlineHint}</div>}
              {!!field.hint && <div className="field-hint">{field.hint({ values })}</div>}

              {!getError(field).errorMsg && <div className="bottom-hint">{field?.help?.()}</div>}
              <div className="field-helper-text">{getError(field).errorMsg}</div>
            </div>
          </div>
        ))}
      </div>
      {children?.({
        disabled: formDisabled,
        errors,
        values,
        touched,
        defaultWrapperClass: 'btns-wrapper',
        triggerOnSubmit: handleSubmit,
      })}
    </form>
  );
}

Fields.defaultProps = {
  formStyle: null,
  initialValues: null,
  onValuesChanges: () => {},
  wrapperClass: '',
};

Fields.propTypes = {
  fields: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  validationSchema: PropTypes.object.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  formStyle: PropTypes.object,
  onValuesChanges: PropTypes.func,
  wrapperClass: PropTypes.string,
};
